import React, { useState } from "react";
import { useForm } from "react-hook-form";
import { zodResolver } from "@hookform/resolvers/zod";
import { LoginRequestSchema, findWorkspaceSelection } from "../../../domain/types/auth";
import type { LoginRequest, WorkspaceSelectionResponse } from "../../../domain/types/auth";
import { useAuthStore } from "../../stores/auth-store";
import { Button } from "./Button";
import { Input } from "./Input";
import { useSocialLogin } from "../../hooks/use-social-login";
import { useNavigate } from "react-router-dom";
import { WorkspacePickerDialog, workspaceHomePath } from "../Auth/WorkspacePickerDialog";

interface LoginModalProps {
  isOpen: boolean;
  onClose: () => void;
  companyId?: number | null;
}

export const LoginModal: React.FC<LoginModalProps> = ({
                                                        isOpen,
                                                        onClose,
                                                        companyId,
                                                      }) => {
  const navigate = useNavigate();
  const { login, isLoading, error, clearError } = useAuthStore();
  const [showPassword, setShowPassword] = useState(false);
  const { login: socialLogin } = useSocialLogin();
  const [workspaceSelection, setWorkspaceSelection] = useState<WorkspaceSelectionResponse | null>(null);

  const {
    register,
    handleSubmit,
    formState: { errors },
  } = useForm<LoginRequest>({
    resolver: zodResolver(LoginRequestSchema),
  });

  const onSubmit = async (data: LoginRequest) => {
    try {
      clearError();
      await login(data);
      navigate("/");
      onClose(); // 로그인 성공 시 모달 닫기
    } catch (error) {
      // 여러 회사에 속한 계정이면 들어갈 회사를 고르게 한다. 그 외 에러는 store에서 이미 처리됨
      const selection = findWorkspaceSelection(error);
      if (selection) setWorkspaceSelection(selection);
    }
  };

  if (workspaceSelection) {
    return (
      <WorkspacePickerDialog
        selection={workspaceSelection}
        onClose={() => setWorkspaceSelection(null)}
        onCompleted={(_, workspace) => {
          setWorkspaceSelection(null);
          navigate(workspaceHomePath(workspace));
          onClose();
        }}
      />
    );
  }

  if (!isOpen) return null;

  return (
    <div className="fixed inset-0 z-50 flex items-center justify-center">
      {/* Backdrop */}
      <div
        className="absolute inset-0 bg-black bg-opacity-50"
        onClick={onClose}
      />

      {/* Modal Content */}
      <div className="relative bg-white shadow-xl mx-4 rounded-[1.25rem] p-[20px]">
        {/* Close Button */}

        <button
          onClick={onClose}
          className="absolute top-4 right-4 text-gray-400 hover:text-gray-600"
        >
          <svg
            className="w-6 h-6"
            fill="none"
            stroke="currentColor"
            viewBox="0 0 24 24"
          >
            <path
              strokeLinecap="round"
              strokeLinejoin="round"
              strokeWidth={2}
              d="M6 18L18 6M6 6l12 12"
            />
          </svg>
        </button>

        <div
          className="mb-3"
          style={{
            display: "flex",
            flexDirection: "column",
            alignItems: "center",
          }}
        >
          <div
            className="text-left mb-6"
            style={{ marginTop: "24px", width: "320px" }}
          >
            <div className="">
              <p
                className="text-gray-900 font-['Pretendard'] font-normal"
                style={{ fontSize: "24px" }}
              >
                서비스 사용을 위해
              </p>
              <p
                className="text-gray-900 font-['Pretendard'] font-semibold"
                style={{ fontSize: "24px" }}
              >
                로그인이 필요해요.
              </p>
            </div>
          </div>

          <form className="text-left" onSubmit={handleSubmit(onSubmit)}>
            {error && (
              <div className="bg-red-50 border border-red-200 rounded-md p-3 mb-3">
                <div className="text-sm text-red-600">
                  {error.message || "로그인에 실패했습니다."}
                </div>
              </div>
            )}

            <div>
              {/* 아이디 또는 이메일 */}
              <div>
                <Input
                  type="email"
                  placeholder="이메일"
                  autoComplete="email"
                  leftIcon={
                    <img
                      src="/assets/icons/user.svg"
                      alt="User"
                      className="w-5 h-5"
                    />
                  }
                  {...register("email")}
                  error={errors.email?.message}
                  style={{
                    width: "320px",
                    height: "40px",
                    borderRadius: "8px",
                  }}
                />
              </div>

              {/* 비밀번호 */}
              <div style={{ marginTop: "12px" }}>
                <Input
                  type={showPassword ? "text" : "password"}
                  placeholder="비밀번호"
                  autoComplete="current-password"
                  leftIcon={
                    <img
                      src="/assets/icons/password-lock.svg"
                      alt="Password"
                      className="w-5 h-5"
                    />
                  }
                  rightIcon={
                    <button
                      type="button"
                      className="text-xs sm:text-sm text-gray-500 hover:text-gray-700"
                      onClick={() => setShowPassword(!showPassword)}
                    >
                      {showPassword ? "숨기기" : "보기"}
                    </button>
                  }
                  {...register("password")}
                  error={errors.password?.message}
                  style={{
                    width: "320px",
                    height: "40px",
                    borderRadius: "8px",
                  }}
                />
                <div className="flex justify-end mt-2">
                  <a
                    href="/auth/forgot-password"
                    className="text-sm text-[#4285F4] hover:underline"
                    onClick={(e) => {
                      e.preventDefault();
                      onClose();
                      navigate("/auth/forgot-password");
                    }}
                  >
                    비밀번호를 잊으셨나요?
                  </a>
                </div>
              </div>
            </div>

            {/* 로그인 버튼 */}
            <div className="mt-8">
              <Button
                type="submit"
                loading={isLoading}
                className="bg-blue-600 hover:bg-blue-700 text-white font-['Pretendard'] font-semibold transition-colors"
                style={{
                  width: "320px",
                  height: "48px",
                  borderRadius: "8px",
                  fontSize: "16px",
                }}
              >
                로그인
              </Button>
            </div>

            {/* 구분선 */}
            <div className="relative" style={{ marginTop: "25px" }}>
              <div
                className="w-full border-t border-gray-200"
                style={{ width: "320px" }}
              ></div>
            </div>

            {/* SNS 로그인 버튼들 */}
            <div
              className="flex justify-center"
              style={{ marginTop: "8px", gap: "40px" }}
            >
              <button
                type="button"
                className="transition-transform hover:scale-105"
                onClick={() => {
                  socialLogin("naver");
                }}
              >
                <img
                  src="/assets/icons/naver-login.svg"
                  alt="네이버 로그인"
                  style={{ width: "68px", height: "68px" }}
                />
              </button>
              <button
                type="button"
                className="transition-transform hover:scale-105"
                onClick={() => {
                  socialLogin("kakao");
                }}
              >
                <img
                  src="/assets/icons/kakao-login.svg"
                  alt="카카오 로그인"
                  style={{ width: "68px", height: "68px" }}
                />
              </button>
              <button
                type="button"
                className="transition-transform hover:scale-105"
                onClick={() => {
                  socialLogin("google");
                }}
              >
                <img
                  src="/assets/icons/google-login.svg"
                  alt="구글 로그인"
                  style={{ width: "68px", height: "68px" }}
                />
              </button>
            </div>
          </form>
        </div>
      </div>
    </div>
  );
};
